// src/contexts/VisitContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Visit, Event, Filter } from "@visitproof/types";
import { VisitService } from "../services/VisitService";
import { useAccount } from "./AccountContext";
import { useAuth } from "./AuthContext";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface VisitContextProps {
  loading: Loadings;
  error: Errors;
  filter: Filter | null;
  setFilter: (filter: Filter | null) => void;
  visits: Visit[];
  events: Event[];
  selectedVisit: Visit | null;
  createVisit: (
    visitData: Omit<
      Visit,
      "id" | "createdAt" | "updatedAt" | "accountId" | "createdBy"
    >,
  ) => Promise<string>;
  updateVisit: (
    visitId: string,
    updatedData: Partial<Omit<Visit, "id" | "createdAt">>,
  ) => Promise<string>;
  deleteVisitById: (visitId: string) => void;
  setSelectedVisit: (visitId: string) => void;
}

const VisitContext = createContext<VisitContextProps | undefined>(undefined);

export const useVisit = (): VisitContextProps => {
  const context = useContext(VisitContext);
  if (!context) {
    throw new Error("useVisit must be used within a VisitProvider");
  }
  return context;
};

export const VisitProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { account } = useAccount();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter | null>(null);
  const [loading, setLoading] = useState<Loadings>({
    list: true,
    details: true,
  });
  const [error, setError] = useState<Errors>({});

  useEffect(() => {
    if (!user) {
      setLoading((prev) => ({ ...prev, list: false }));
      return;
    }

    let unsubscribe: (() => void) | undefined;
    let isCanceled = false;

    const setupVisitsListener = async () => {
      try {
        setLoading((prev) => ({ ...prev, list: true }));
        setError((prev) => ({ ...prev, list: null }));

        // Get claims from Firebase Auth token
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.log("VisitContext: No current user");
          setLoading((prev) => ({ ...prev, list: false }));
          return;
        }

        const tokenResult = await currentUser.getIdTokenResult(true);

        // Check if effect was canceled while awaiting
        if (isCanceled) return;

        const claims = tokenResult.claims;
        const role = claims.role as string | undefined;
        const accountIds = claims.accountIds as string[] | undefined;
        // Honor the account selected in AccountContext (persisted via
        // localStorage) when the user actually has access to it; otherwise
        // fall back to the first account in the claims.
        const accountId =
          account?.id && accountIds?.includes(account.id)
            ? account.id
            : accountIds?.[0];
        const supervisorIds = claims.supervisorIds as string[] | undefined;
        const parentIds = claims.parentIds as string[] | undefined;

        console.log("VisitContext: Claims", { role, accountId, supervisorIds, parentIds });

        let visitsQuery;

        if (role === "admin" && accountId) {
          // Admin: query by accountId from claims
          visitsQuery = query(
            collection(db, "visits"),
            where("accountId", "==", accountId),
            ...(filter?.caseId ? [where("caseId", "==", filter.caseId)] : []),
            ...(filter?.supervisorId
              ? [where("supervisorId", "==", filter.supervisorId)]
              : []),
            orderBy("startDate", "desc"),
          );
        } else if (role === "supervisor" && supervisorIds && supervisorIds.length > 0) {
          // Supervisor: query by supervisorIds array
          // Firestore 'in' query supports up to 30 values
          visitsQuery = query(
            collection(db, "visits"),
            where("supervisorId", "in", supervisorIds),
            ...(filter?.caseId ? [where("caseId", "==", filter.caseId)] : []),
            orderBy("startDate", "desc"),
          );
        } else if (role === "parent" && parentIds && parentIds.length > 0) {
          // Parent: query by parentIds using array-contains-any
          // Firestore 'array-contains-any' supports up to 30 values
          visitsQuery = query(
            collection(db, "visits"),
            where("parentIds", "array-contains-any", parentIds),
            ...(filter?.caseId ? [where("caseId", "==", filter.caseId)] : []),
            orderBy("startDate", "desc"),
          );
        } else if (account?.id) {
          // Fallback: use account.id (for admins without claims set)
          visitsQuery = query(
            collection(db, "visits"),
            where("accountId", "==", account.id),
            ...(filter?.caseId ? [where("caseId", "==", filter.caseId)] : []),
            ...(filter?.supervisorId
              ? [where("supervisorId", "==", filter.supervisorId)]
              : []),
            orderBy("startDate", "desc"),
          );
        } else {
          console.log("VisitContext: No valid query parameters", { role, accountId, supervisorIds, parentIds, accountId2: account?.id });
          setLoading((prev) => ({ ...prev, list: false }));
          return;
        }

        console.log("VisitContext: Setting up listener for role:", role);

        unsubscribe = onSnapshot(
          visitsQuery,
          (docSnapshot) => {
            if (isCanceled) return;
            const visitList: Visit[] = [];
            docSnapshot.forEach((doc) => {
              const visit = doc.data() as Visit;
              visit.id = doc.id;
              visitList.push(visit);
            });
            console.log("VisitContext: Loaded visits", { count: visitList.length, role });
            setVisits(visitList);
            setLoading((prev) => ({ ...prev, list: false }));
          },
          (err) => {
            if (isCanceled) return;
            console.error("Error fetching visits: ", err);
            setError((prev) => ({ ...prev, list: err }));
            setLoading((prev) => ({ ...prev, list: false }));
          },
        );
      } catch (err) {
        if (isCanceled) return;
        console.error("Error setting up visits listener:", err);
        setError((prev) => ({ ...prev, list: err as Error }));
        setLoading((prev) => ({ ...prev, list: false }));
      }
    };

    setupVisitsListener();

    return () => {
      isCanceled = true;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user, filter, account?.id]);

  const setSelectedVisitById = (visitId: string) => {
    setSelectedVisitId(visitId); // Set the visitId to trigger the listener
  };

  const createVisit = async (
    caseData: Omit<
      Visit,
      "id" | "createdAt" | "updatedAt" | "accountId" | "createdBy"
    >,
  ): Promise<string> => {
    if (!account?.id) {
      throw new Error("No account selected. Cannot create visit.");
    }
    const newCaseId = await VisitService.createVisit({
      ...caseData,
      accountId: account.id,
      createdBy: user!.uid,
    });
    return newCaseId;
  };

  const updateVisit = async (
    visitId: string,
    updatedData: Partial<Omit<Visit, "id" | "createdAt">>,
  ): Promise<string> => {
    await VisitService.updateVisit(visitId, updatedData);
    setSelectedVisitById(visitId);
    return visitId;
  };

  const deleteVisitById = async (visitId: string) => {
    await VisitService.deleteVisit(visitId);
  };

  useEffect(() => {
    if (!selectedVisitId) return;
    setError((prev) => ({ ...prev, details: null }));
    setLoading((prev) => ({ ...prev, details: true }));
    const docRef = doc(db, `visits/${selectedVisitId}`);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setSelectedVisit(
            (prev) => ({ id: docSnapshot.id, ...data }) as Visit,
          );
          setLoading((prev) => ({ ...prev, details: false }));
        } else {
          setError((prev) => ({
            ...prev,
            details: new Error("Visit not found"),
          }));
          setLoading((prev) => ({ ...prev, details: false }));
        }
      },
      (err) => {
        console.error("Error fetching visit: ", err);
        setError((prev) => ({ ...prev, details: err }));
        setLoading((prev) => ({ ...prev, details: false }));
      },
    );
    return () => {
      unsubscribe();
    };
  }, [selectedVisitId]);

  useEffect(() => {
    if (!selectedVisitId) return;

    let unsubscribe: (() => void) | undefined;

    const setupEventsListener = async () => {
      try {
        // Get claims from Firebase Auth token
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.log("VisitContext events: No current user");
          return;
        }

        const tokenResult = await currentUser.getIdTokenResult(true);
        const claims = tokenResult.claims;
        const role = claims.role as string | undefined;
        const accountIds = claims.accountIds as string[] | undefined;
        // Honor the AccountContext-selected account when the user has access
        // to it, so events match the currently viewed account.
        const accountId =
          account?.id && accountIds?.includes(account.id)
            ? account.id
            : accountIds?.[0] || account?.id;

        // Parents should not see events - they can only see limited visit details
        if (role === "parent") {
          console.log("VisitContext events: Skipping events load for parent user");
          setEvents([]);
          return;
        }

        if (!accountId) {
          console.log("VisitContext events: No accountId available");
          return;
        }

        const eventsQuery = query(
          collection(db, "events"),
          where("accountId", "==", accountId),
          where("visitId", "==", selectedVisitId),
          orderBy("createdAt", "desc"),
        );

        unsubscribe = onSnapshot(
          eventsQuery,
          (docSnapshot) => {
            const eventList: Event[] = [];
            docSnapshot.forEach((doc) => {
              const event = doc.data() as Event;
              event.id = doc.id;
              eventList.push(event);
            });
            setEvents(eventList);
          },
          (err) => {
            console.error("Error fetching events: ", err);
            setError((prev) => ({ ...prev, details: err }));
          },
        );
      } catch (err) {
        console.error("Error setting up events listener:", err);
      }
    };

    setupEventsListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [selectedVisitId, account?.id]);

  return (
    <VisitContext.Provider
      value={{
        filter,
        setFilter,
        visits,
        events,
        selectedVisit,
        setSelectedVisit: setSelectedVisitById,
        loading,
        error,
        createVisit,
        updateVisit,
        deleteVisitById,
      }}
    >
      {children}
    </VisitContext.Provider>
  );
};
